// Minnesotaänger Mini App — message API contract types.
// Contract source of truth: minnesanger-miniapp-v1/03-api-draft.md (shared contract).
// New-in-miniapp: this file is written for this project, not from telegram-tt.

export type ApiActionStatus = 'allowed' | 'blocked';

export interface ApiMessageActionStates {
  reply: ApiActionStatus;
  copy: ApiActionStatus;
  select: ApiActionStatus;
  reroll: ApiActionStatus;
  edit: ApiActionStatus;
  delete: ApiActionStatus;
}

export type ApiMessageRole = 'user' | 'assistant';

// Unified message object (03-api-draft.md §4)
export interface ApiMiniAppMessage {
  event_id: number;
  reply_group_id: string;
  bubble_index: number;
  assistant_id: string | null;
  role: ApiMessageRole;
  text: string;
  created_at: string; // ISO 8601 UTC
  message_id: number | null; // TG message id when delivered, null otherwise
  visibility: 'visible' | 'tombstone';
  delivery_status: 'sent' | 'pending' | 'failed' | 'delivered' | null;
  actions: ApiMessageActionStates;
}

export interface ApiMiniAppConversation {
  chat_id: string; // e.g. "telegram:dm:gilbert:8712498712"
  assistant_id: string; // e.g. "gilbert"
  title: string;
  last_message_preview: string;
  last_message_at: string | null;
  unread_count: number | null; // null until read-receipt semantics exist
}

export interface ApiMiniAppHistoryResponse {
  messages: ApiMiniAppMessage[];
  next_before_event_id: number | null;
}

export interface ApiMiniAppConversationsResponse {
  conversations: ApiMiniAppConversation[];
}

export type ApiMiniAppSendRequest = {
  chat_id: string;
  assistant_id: string;
  text: string;
  // v1 (fix-list E): replies reference the stable event_id (l0 rowid), not
  // the platform message id — the bridge resolves it server-side.
  reply_to_event_id?: number;
};

export interface ApiMiniAppAcceptedResponse {
  accepted: true;
  status: 'queued';
}

export interface ApiMiniAppTargetRequest {
  chat_id: string;
  event_id: number;
}

export interface ApiMiniAppEditRequest extends ApiMiniAppTargetRequest {
  text: string;
}

export type ApiMiniAppRequest =
  | ApiMiniAppSendRequest
  | ApiMiniAppTargetRequest
  | ApiMiniAppEditRequest;

// Unified error object (03-api-draft.md §7)
export type ApiErrorCode =
  | 'OK'
  | 'TARGET_NOT_FOUND'
  | 'TARGET_ALREADY_TOMBSTONED'
  | 'TARGET_SUPERSEDED'
  | 'EDIT_BLOCKED_BY_MEMORY'
  | 'REROLL_BLOCKED'
  | 'TELEGRAM_EDIT_FAILED'
  | 'TELEGRAM_DELETE_FAILED'
  | 'DELIVERY_UNKNOWN'
  | 'PERSIST_FAILED'
  | 'AUTH_INVALID'
  | 'FORBIDDEN'
  | 'INTERNAL';

export interface ApiMiniAppErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
    detail?: Record<string, unknown>;
  };
}

export class ApiMiniAppError extends Error {
  code: ApiErrorCode;

  detail?: Record<string, unknown>;

  constructor(code: ApiErrorCode, message: string, detail?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiMiniAppError';
    this.code = code;
    this.detail = detail;
  }
}

// Actions availability helper — frontend renders the six-item menu uniformly
// and lets the server decide per-message availability.
export function isBlockedByBackend(actions: ApiMessageActionStates): boolean {
  return Object.values(actions).some((status) => status === 'blocked');
}
